import { isDisabledSignal, isFailingSignal } from 'core/utils/predicates';
import { format, parseISO } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactJson from 'react-json-view';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { RobotViewContext } from '../../../context/RobotView';
import { a11yProps } from '../../Utils';
import AlertBox from '../../tools/AlertBox';
import LineChartCard from '../../tools/LineChartCard';
import TabPanelv2 from '../../tools/TabPanelv2';
import TrackerCard from '../../tools/TrackerCard';

const InfoRow = (props) => {
  const { data, name, sectionLabel, handleChart, isLoading, selectedKey } =
    props;
  const [openDetail, setOpenDetail] = useState(false);

  // Signal key for a row, matching what handleChart emits; used to highlight
  // the row whose chart is currently shown.
  const rowKey = (entry) =>
    entry[2] ?? (entry[0] === name ? entry[0] : `${entry[0]}_${name}`);
  const isSelected = (entry) =>
    selectedKey != null && rowKey(entry) === selectedKey;

  const iconComponentState = (data) => {
    if (!data.length) {
      return <ErrorIcon color="warning" sx={{ fontSize: '20px' }} />;
    }
    // is_enabled === false means disabled, not an error.
    const failing = data.some((val) => isFailingSignal(val[0], val[1]));
    if (failing) {
      return <CancelIcon color="error" sx={{ fontSize: '20px' }} />;
    }
    const disabled = data.some((val) => isDisabledSignal(val[0], val[1]));
    if (disabled) {
      return <RemoveCircleIcon color="disabled" sx={{ fontSize: '20px' }} />;
    }
    return <CheckCircleIcon sx={{ fontSize: '20px', color: '#0E7F19' }} />;
  };

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          bgcolor:
            data.length === 1 && isSelected(data[0])
              ? 'action.selected'
              : 'transparent',
        }}
      >
        {data.length === 1 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            {iconComponentState(data)}
            <Tooltip title={sectionLabel || ''}>
              <Typography>{data[0][0]}</Typography>
            </Tooltip>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'flex-end',
              }}
            >
              {typeof data[0][1] === 'boolean' ? (
                <IconButton
                  aria-label="chart_icon"
                  size="small"
                  onClick={() =>
                    handleChart({
                      statusKey: data[0][2] ?? data[0][0],
                      statusType: 'boolean',
                    })
                  }
                >
                  <IconComponent iconName="stats-chart" />
                </IconButton>
              ) : (
                <IconButton
                  aria-label="chart_icon"
                  size="small"
                  onClick={() =>
                    handleChart({
                      statusKey: data[0][2] ?? data[0][0],
                      statusType: 'number',
                    })
                  }
                >
                  <IconComponent iconName="analytics" />
                </IconButton>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {iconComponentState(data)}
              <Tooltip title={sectionLabel || ''}>
                <Typography>{name}</Typography>
              </Tooltip>
            </Box>
            {openDetail ? (
              <KeyboardArrowDownIcon
                sx={{ cursor: 'pointer' }}
                onClick={() => setOpenDetail(!openDetail)}
              />
            ) : (
              <KeyboardArrowRightIcon
                sx={{ cursor: 'pointer' }}
                onClick={() => setOpenDetail(!openDetail)}
              />
            )}
          </Box>
        )}
      </Box>
      {openDetail ? (
        <Box sx={{ marginLeft: 5 }}>
          {data.map((info, idx) => (
            <Box
              key={idx}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                bgcolor: isSelected(info) ? 'action.selected' : 'transparent',
              }}
            >
              {typeof info[1] === 'boolean' &&
                (isDisabledSignal(info[0], info[1]) ? (
                  <RemoveCircleIcon color="disabled" sx={{ fontSize: '20px' }} />
                ) : info[1] ? (
                  <CheckCircleIcon sx={{ fontSize: '20px', color: '#0E7F19' }} />
                ) : (
                  <CancelIcon color="error" sx={{ fontSize: '20px' }} />
                ))}
              <Typography>
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  {info[0]}
                </Box>
                {typeof info[1] !== 'boolean' ? `: ${info[1]}` : ''}
              </Typography>
              <IconButton
                sx={{ ml: 'auto' }}
                aria-label="chart_icon"
                size="small"
                onClick={() =>
                  handleChart({
                    statusKey:
                      info[2] ??
                      (info[0] === name ? info[0] : `${info[0]}_${name}`),
                    statusType:
                      typeof info[1] === 'boolean' ? 'boolean' : 'number',
                  })
                }
              >
                <IconComponent
                  iconName={
                    typeof info[1] === 'boolean' ? 'stats-chart' : 'analytics'
                  }
                />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

function RobotStatusModal(props) {
  const {
    open,
    setOpen,
    data,
    robotCode,
    robotStatusHistory,
    getRobotStatusHistory,
    isLoadingRobotStatusHistory,
  } = props;
  const [tabIndex, setTabIndex] = useState(0);
  const { t } = useTranslation();
  const [statusInfo, setStatusInfo] = useState(null);
  const [currentBooleanData, setCurrentBooleanData] = useState(null);
  const [timeFrame, setTimeFrame] = useState({ deltaTime: 1, params: {} });

  const { rawRobotStatus, statusTabs } = useContext(RobotViewContext);

  useEffect(() => {
    if (statusInfo && robotStatusHistory) {
      const currentBooleanData = robotStatusHistory?.boolean_data?.map(
        (item) => {
          if (item[statusInfo.statusKey]) {
            return {
              ...item[statusInfo.statusKey],
              tooltip: `${item[statusInfo.statusKey]?.tooltip} ${format(
                parseISO(`${item.timestamp}Z`),
                'yyyy-MM-dd HH:mm:ss'
              )}`,
            };
          } else {
            return { color: '', tooltip: '' };
          }
        }
      );
      setCurrentBooleanData(currentBooleanData);
    }
  }, [statusInfo, robotStatusHistory]);

  useEffect(() => {
    if (
      robotCode &&
      !isLoadingRobotStatusHistory &&
      open &&
      timeFrame.deltaTime
    ) {
      getRobotStatusHistory({
        robotCode: robotCode,
        hoursBefore: timeFrame.deltaTime,
      });
    }
  }, [timeFrame.deltaTime, open]);

  const handleChart = (statusInfo) => {
    setStatusInfo({
      statusKey: statusInfo.statusKey,
      statusType: statusInfo.statusType,
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Clear the selected chart when switching tabs.
    setStatusInfo(null);
    setCurrentBooleanData(null);
  };

  const handleOnClose = () => {
    setOpen(false);
    setStatusInfo(null);
    setCurrentBooleanData(null);
    setTimeFrame({ deltaTime: 1, params: {} });
  };

  const emptyAlert = (text) => (
    <AlertBox severity="info" sx={{ m: 1 }} content={text} />
  );

  const sectionLabels = {
    devices: t('overseer_app.widget.section_devices', 'Devices'),
    mobile_base: t('overseer_app.widget.section_mobile_base', 'Mobile Base'),
  };

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '80vh' } }}
    >
      <Box sx={{ p: 2 }}>
        <Typography fontSize={20} marginLeft={1} fontWeight={'bold'}>
          {t('overseer_app.widget.robot_status', 'Robot Status')}
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabIndex} onChange={handleTabChange}>
            <Tab label={t('overseer_app.widget.legacy', 'Legacy')} {...a11yProps(0, {})} />
            <Tab label={t('overseer_app.widget.devices', 'Devices')} {...a11yProps(1, {})} />
            <Tab label={t('overseer_app.widget.extra', 'Extra')} {...a11yProps(2, {})} />
            <Tab label={t('overseer_app.widget.all_info', 'All Info')} {...a11yProps(3, {})} />
          </Tabs>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mt: 1,
          }}
        >
          {/* Left column: status lists */}
          <Box
            sx={{
              flex: { md: tabIndex === 3 ? 1 : '0 0 45%' },
              width: { xs: '100%', md: 'auto' },
              overflowY: 'auto',
              height: { xs: 'auto', md: '65vh' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Legacy: old-format robots. TODO(deprecate): remove with legacy format. */}
            <TabPanelv2 value={tabIndex} index={0}>
              {data && data.length
                ? data.map((info, idx) => (
                  <InfoRow
                    isLoading={isLoadingRobotStatusHistory}
                    data={info['data']}
                    name={info['name']}
                    key={idx}
                    handleChart={handleChart}
                    selectedKey={statusInfo?.statusKey}
                  />
                ))
                : emptyAlert(
                  t(
                    'overseer_app.widget.no_legacy_status',
                    'No legacy status available'
                  )
                )}
            </TabPanelv2>

            {/* Devices + mobile_base: new-format robots */}
            <TabPanelv2 value={tabIndex} index={1}>
              {statusTabs?.devices?.some((group) => group.instances.length)
                ? statusTabs.devices.flatMap((group) =>
                  group.instances.map((instance) => (
                    <InfoRow
                      key={`${group.section}-${group.group}-${instance.name}`}
                      isLoading={isLoadingRobotStatusHistory}
                      name={instance.name}
                      sectionLabel={sectionLabels[group.section]}
                      data={instance.metrics.map((item) => [
                        item.metric,
                        item.value,
                        item.statusKey,
                      ])}
                      handleChart={handleChart}
                      selectedKey={statusInfo?.statusKey}
                    />
                  ))
                )
                : emptyAlert(
                  t(
                    'overseer_app.widget.no_device_status',
                    'No device data available'
                  )
                )}
            </TabPanelv2>

            {/* Extra: top-level status flags from new-format robots */}
            <TabPanelv2 value={tabIndex} index={2}>
              {statusTabs?.extra?.length
                ? statusTabs.extra.map((item, idx) => (
                  <InfoRow
                    isLoading={isLoadingRobotStatusHistory}
                    data={[[item.name, item.value]]}
                    name={item.name}
                    key={idx}
                    handleChart={handleChart}
                    selectedKey={statusInfo?.statusKey}
                  />
                ))
                : emptyAlert(
                  t(
                    'overseer_app.widget.no_extra_status',
                    'No extra data available'
                  )
                )}
            </TabPanelv2>

            {/* Raw payload */}
            <TabPanelv2 value={tabIndex} index={3}>
              <ReactJson
                src={rawRobotStatus}
                sortKeys
                collapsed={1}
                displayDataTypes={false}
                displayObjectSize={false}
                name={false}
                quotesOnKeys={false}
                style={{ fontSize: '12px' }}
                iconStyle="circle"
              />
            </TabPanelv2>
          </Box>

          {/* Vertical divider on desktop; mobile uses the chart's top border */}
          {tabIndex !== 3 && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', md: 'block' } }}
            />
          )}

          {/* Right column: history chart for the selected signal (hidden on raw tab) */}
          {tabIndex !== 3 && (
            <Box
              sx={{
                flex: { md: 1 },
                width: { xs: '100%', md: 'auto' },
                overflowY: 'auto',
                height: { xs: 'auto', md: '65vh' },
                minHeight: 300,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'safe center',
                borderTop: { xs: 1, md: 0 },
                borderColor: 'divider',
              }}
            >
              {isLoadingRobotStatusHistory ? (
                <Box sx={{ width: '100%', p: 2 }}>
                  <Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} />
                  <Skeleton
                    variant="text"
                    width="25%"
                    sx={{ mx: 'auto', mb: 2 }}
                  />
                  <Skeleton variant="rounded" width="100%" height={280} />
                </Box>
              ) : statusInfo && robotStatusHistory ? (
                statusInfo.statusType === 'boolean' ? (
                  <TrackerCard
                    sx={{ width: '100%', boxShadow: 0, mt: 0 }}
                    title={t(
                      'overseer_app.widget.historic_robot_status',
                      'Historic Robot Status'
                    )}
                    subtitle={t(
                      'overseer_app.widget.history_source',
                      'Source: Cloud Logging'
                    )}
                    leftText={t(
                      `overseer_app.widget.${statusInfo.statusKey}`,
                      statusInfo.statusKey
                    )}
                    rightTest="Uptime"
                    noDataText={t('overseer_app.widget.no_data', 'No data')}
                    trackerData={currentBooleanData}
                    isLoading={isLoadingRobotStatusHistory}
                    timeFrame={timeFrame}
                    setTimeFrame={setTimeFrame}
                  />
                ) : (
                  <LineChartCard
                    sx={{ width: '100%', boxShadow: 0, mt: 0 }}
                    title={t(
                      'overseer_app.widget.historic_robot_status',
                      'Historic Robot Status'
                    )}
                    subtitle={t(
                      'overseer_app.widget.history_source',
                      'Source: Cloud Logging'
                    )}
                    inputIndex={'timestamp'}
                    noDataText={t('overseer_app.widget.no_data', 'No data')}
                    inputCategories={[statusInfo.statusKey]}
                    lineData={robotStatusHistory.numeric_data}
                    isLoading={isLoadingRobotStatusHistory}
                    timeFrame={timeFrame}
                    setTimeFrame={setTimeFrame}
                  />
                )
              ) : (
                <Box sx={{ width: '100%', p: 2 }}>
                  {emptyAlert(
                    t(
                      'overseer_app.widget.select_chart_data',
                      'Select a status to see the chart data'
                    )
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default RobotStatusModal;
